import React, { useState, useEffect } from "react";
import {
  Box, Typography, FormControl, InputLabel, Select,
  MenuItem, Button, Paper, TextField, IconButton, List, ListItem,
  Tabs, Tab
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import api from "../../utils/api";
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";


const IntegrationSettings = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [frequency, setFrequency] = useState("manual");
  const [exportTime, setExportTime] = useState(new Date());
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [mappings, setMappings] = useState([]);
  const [editedMappings, setEditedMappings] = useState({});
  const [newMapping, setNewMapping] = useState({ type_id: "", item_group_code: "" });

  const [memberTypeMappings, setMemberTypeMappings] = useState([]);
  const [editedMemberMappings, setEditedMemberMappings] = useState({});
  const [newMemberMapping, setNewMemberMapping] = useState({
    type_pattern: "",
    mapped_member_number: "",
    description: ""
  });

  useEffect(() => {
    fetchSettings();
    fetchMappings();
    fetchMemberTypeMappings();
  }, []);


  const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});


  const fetchSettings = async () => {
    try {
      const res = await api.get("/integration-settings");
      setFrequency(res.data.frequency);
      if (res.data.export_hour != null && res.data.export_minute != null) {
        const time = new Date();
        time.setHours(res.data.export_hour);
        time.setMinutes(res.data.export_minute);
        setExportTime(time);
      }
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
  };

  const handleSnackbarClose = () => {
  setSnackbar((prev) => ({ ...prev, open: false }));
};

  const fetchMappings = async () => {
    try {
      const res = await api.get("/item-group-mappings");
      setMappings(res.data);
    } catch (err) {
      console.error("Failed to fetch mappings:", err);
    }
  };

  const fetchMemberTypeMappings = async () => {
    try {
      const res = await api.get("/member-type-mappings");
      setMemberTypeMappings(res.data);
    } catch (err) {
      console.error("Failed to fetch member type mappings:", err);
    }
  };

  const saveSettings = async () => {
    try {
      await api.put("/integration-settings", {
        frequency,
        export_hour: exportTime.getHours(),
        export_minute: exportTime.getMinutes()
      });
     setSnackbar({ open: true, message: "Settings saved successfully!", severity: "success" });

    } catch (err) {
      console.error("Error saving settings:", err);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get("/jonas-exports/download", {
        responseType: "blob"
      });
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jonas_export_${Date.now()}.txt`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Export failed. Please try again.");
    }
  };

  // Item Group Mappings
  const handleEditChange = (id, value) => {
    setEditedMappings(prev => ({ ...prev, [id]: value }));
  };

  const handleUpdateMapping = async (mapping) => {
    try {
      await api.put(`/item-group-mappings/${mapping.id}`, {
        module_type: 1,
        type_id: mapping.type_id,
        item_group_code: editedMappings[mapping.id]
      });
      setSnackbar({ open: true, message: "Mapping updated", severity: "success" });
      const updated = { ...editedMappings };
      delete updated[mapping.id];
      setEditedMappings(updated);
      fetchMappings();
    } catch (err) {
      console.error("Error updating mapping:", err);
    }
  };

  const handleAddMapping = async () => {
    const { type_id, item_group_code } = newMapping;
    if (!type_id || !item_group_code) return alert("Fill both fields");
    try {
      await api.post("/item-group-mappings", {
        module_type: 1,
        type_id,
        item_group_code
      });
      setSnackbar({ open: true, message: "Mapping added", severity: "success" });
      setNewMapping({ type_id: "", item_group_code: "" });
      fetchMappings();
    } catch (err) {
      console.error("Error adding mapping:", err);
    }
  };

  const handleDeleteMapping = async (id) => {
    try {
      await api.delete(`/item-group-mappings/${id}`);
      setSnackbar({ open: true, message: "Mapping deleted", severity: "info" });
      fetchMappings();
    } catch (err) {
      console.error("Error deleting mapping:", err);
    }
  };

  // Member Type Mappings
  const handleMemberEditChange = (id, field, value) => {
    setEditedMemberMappings(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value }
    }));
  };

  const handleUpdateMemberMapping = async (mapping) => {
    try {
      await api.put(`/member-type-mappings/${mapping.id}`, {
        type_pattern: mapping.type_pattern,
        mapped_member_number: mapping.mapped_member_number,
        description: mapping.description
      });
      setSnackbar({ open: true, message: "Member mapping updated", severity: "success" });
      const updated = { ...editedMemberMappings };
      delete updated[mapping.id];
      setEditedMemberMappings(updated);
      fetchMemberTypeMappings();
    } catch (err) {
      console.error("Error updating member mapping:", err);
    }
  };

  const handleAddMemberMapping = async () => {
    const { type_pattern, mapped_member_number } = newMemberMapping;
    if (!type_pattern || !mapped_member_number) return alert("Fill required fields");
    try {
      await api.post("/member-type-mappings", newMemberMapping);
      setSnackbar({ open: true, message: "Member mapping added", severity: "success" });
      setNewMemberMapping({ type_pattern: "", mapped_member_number: "", description: "" });
      fetchMemberTypeMappings();
    } catch (err) {
      console.error("Error adding member mapping:", err);
    }
  };

  const handleDeleteMemberMapping = async (id) => {
    try {
      await api.delete(`/member-type-mappings/${id}`);
      fetchMemberTypeMappings();
      setSnackbar({ open: true, message: "Member mapping deleted", severity: "info" });
    } catch (err) {
      console.error("Error deleting member mapping:", err);
    }
  };

  return (
    <Paper sx={{ p: 4, maxWidth: 800, mx: "auto", mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Integration Panel
      </Typography>

      <Tabs value={tabIndex} onChange={(e, newValue) => setTabIndex(newValue)} sx={{ mb: 3 }}>
        <Tab label="Export Settings" />
        <Tab label="Item Group Mappings" />
        <Tab label="Member Type Mappings" />
      </Tabs>

      {/* Export Settings */}
      {tabIndex === 0 && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Export Frequency</InputLabel>
              <Select value={frequency} onChange={(e) => setFrequency(e.target.value)} label="Export Frequency">
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
              </Select>
            </FormControl>

            <TimePicker
              label="Export Time"
              value={exportTime}
              onChange={setExportTime}
              ampm={false}
              sx={{ mb: 3, width: '100%' }}
            />

            <Button variant="contained" onClick={saveSettings}>
              Save Settings
            </Button>

            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Export Jonas File
              </Typography>
              <Button variant="outlined" onClick={handleExport}>
                Download Export File
              </Button>
            </Box>
          </Box>
        </LocalizationProvider>
      )}

      {/* Item Group Mappings */}
      {tabIndex === 1 && (
        <Box>
          <Typography variant="h6">Work Orders Item Group Mappings</Typography>
          <List>
            {mappings.map((mapping) => (
              <ListItem key={mapping.id} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <ListItemText
                  primary={mapping.type_id === 1 ? "Materials" : mapping.type_id === 2 ? "Labor" : `Type ${mapping.type_id}`}
                />
                <TextField
                  size="small"
                  value={editedMappings[mapping.id] ?? mapping.item_group_code}
                  onChange={(e) => handleEditChange(mapping.id, e.target.value)}
                />
                <IconButton onClick={() => handleUpdateMapping(mapping)}>
                  <SaveIcon />
                </IconButton>
                <IconButton onClick={() => handleDeleteMapping(mapping.id)}>
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>

          <Typography variant="subtitle1" sx={{ mt: 3 }}>
            Add Custom Mapping
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={newMapping.type_id}
                label="Type"
                onChange={(e) => setNewMapping({ ...newMapping, type_id: e.target.value })}
              >
                <MenuItem value={1}>Materials</MenuItem>
                <MenuItem value={2}>Labor</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Item Group Code"
              value={newMapping.item_group_code}
              onChange={(e) => setNewMapping({ ...newMapping, item_group_code: e.target.value })}
            />

            <Button variant="contained" onClick={handleAddMapping}>
              Add
            </Button>
          </Box>
        </Box>
      )}

      {/* Member Type Mappings */}
      {tabIndex === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Member Type Mappings
          </Typography>

          <List>
            {memberTypeMappings.map((mapping) => {
              const editValues = editedMemberMappings[mapping.id] || mapping;
              return (
                <ListItem key={mapping.id} sx={{ flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                    <TextField
                      label="Type Pattern"
                      value={editValues.type_pattern}
                      onChange={(e) => handleMemberEditChange(mapping.id, "type_pattern", e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Mapped Member Number"
                      value={editValues.mapped_member_number}
                      onChange={(e) => handleMemberEditChange(mapping.id, "mapped_member_number", e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Description"
                      value={editValues.description || ""}
                      onChange={(e) => handleMemberEditChange(mapping.id, "description", e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <IconButton onClick={() => handleUpdateMemberMapping(editValues)} color="primary">
                      <SaveIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteMemberMapping(mapping.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItem>
              );
            })}
          </List>

          <Typography variant="subtitle1" sx={{ mt: 3 }}>
            Add Member Type Mapping
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
            <TextField
              label="Type Pattern"
              value={newMemberMapping.type_pattern}
              onChange={(e) => setNewMemberMapping({ ...newMemberMapping, type_pattern: e.target.value })}
            />
            <TextField
              label="Mapped Member Number"
              value={newMemberMapping.mapped_member_number}
              onChange={(e) => setNewMemberMapping({ ...newMemberMapping, mapped_member_number: e.target.value })}
            />
            <TextField
              label="Description"
              value={newMemberMapping.description}
              onChange={(e) => setNewMemberMapping({ ...newMemberMapping, description: e.target.value })}
            />
            <Button variant="contained" onClick={handleAddMemberMapping}>
              Add
            </Button>
          </Box>
        </Box>
      )}
<Snackbar
  open={snackbar.open}
  autoHideDuration={3000}
  onClose={handleSnackbarClose}
  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
>
  <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
    {snackbar.message}
  </Alert>
</Snackbar>



    </Paper>
  );
};

export default IntegrationSettings;
